import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  Api,
  CompanyDto,
  CompanyTypeDto,
  createOrganization,
  getCompanyTypes,
  getOrganization,
  OrganizationEntity,
  searchOrganization,
  updateOrganization,
} from '@taxora-api';
import { GetOrganizationOptions } from '@taxora-business-core/data-access/state/app.actions';
import { firstValueFrom } from 'rxjs';
import {
  ClearSearchedOrganization,
  CreateOrganization,
  GetCompanyTypes,
  GetOrganizationById,
  SearchOrganizationByCui,
  UpdateOrganization,
} from './organization.actions';

export interface OrganizationStateModel {
  organization: OrganizationEntity | undefined;
  isLoading: boolean;
  companyTypes: CompanyTypeDto[];
  searchedOrganization: CompanyDto | null;
  isSearchingOrganization: boolean;
}

@State<OrganizationStateModel>({
  name: 'organizationState',
  defaults: {
    organization: undefined,
    isLoading: false,
    companyTypes: [],
    searchedOrganization: null,
    isSearchingOrganization: false,
  },
})
@Injectable()
export class OrganizationState {
  private readonly api = inject(Api);

  @Selector()
  static organization(state: OrganizationStateModel): OrganizationEntity | undefined {
    return state.organization;
  }

  @Selector()
  static isLoading(state: OrganizationStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static companyTypes(state: OrganizationStateModel): CompanyTypeDto[] {
    return state.companyTypes;
  }

  @Selector()
  static searchedOrganization(state: OrganizationStateModel): CompanyDto | null {
    return state.searchedOrganization;
  }

  @Selector()
  static isSearchingOrganization(state: OrganizationStateModel): boolean {
    return state.isSearchingOrganization;
  }

  @Action(GetOrganizationById)
  async getOrganizationById(
    { patchState }: StateContext<OrganizationStateModel>,
    { organizationId }: GetOrganizationById,
  ): Promise<void> {
    patchState({ isLoading: true });

    try {
      const organization = await this.api.invoke(getOrganization, { id: organizationId });
      patchState({ organization });
    } catch (error) {
      console.error('Failed to fetch organization', error);
    } finally {
      patchState({ isLoading: false });
    }
  }

  @Action(CreateOrganization)
  async createOrganization(
    { patchState, dispatch }: StateContext<OrganizationStateModel>,
    { payload }: CreateOrganization,
  ): Promise<void> {
    patchState({ isLoading: true });

    try {
      const organization = await this.api.invoke(createOrganization, { body: payload });
      patchState({ organization });
      await firstValueFrom(dispatch(new GetOrganizationOptions()));
    } catch (error) {
      console.error('Failed to create organization', error);
      throw error;
    } finally {
      patchState({ isLoading: false });
    }
  }

  @Action(UpdateOrganization)
  async updateOrganization(
    { patchState, dispatch }: StateContext<OrganizationStateModel>,
    { organizationId, payload }: UpdateOrganization,
  ): Promise<void> {
    patchState({ isLoading: true });

    try {
      const organization = await this.api.invoke(updateOrganization, {
        id: organizationId,
        body: payload,
      });

      patchState({ organization });
      await firstValueFrom(dispatch(new GetOrganizationOptions()));
    } catch (error) {
      console.error('Failed to update organization', error);
      throw error;
    } finally {
      patchState({ isLoading: false });
    }
  }

  @Action(SearchOrganizationByCui)
  async searchOrganizationByCui(
    { patchState }: StateContext<OrganizationStateModel>,
    { cui }: SearchOrganizationByCui,
  ): Promise<void> {
    patchState({ isSearchingOrganization: true, searchedOrganization: null });

    try {
      const response = await this.api.invoke(searchOrganization, { cui });
      patchState({ searchedOrganization: response });
    } catch {
      patchState({ searchedOrganization: null });
    } finally {
      patchState({ isSearchingOrganization: false });
    }
  }

  @Action(ClearSearchedOrganization)
  clearSearchedOrganization({ patchState }: StateContext<OrganizationStateModel>): void {
    patchState({ searchedOrganization: null });
  }

  @Action(GetCompanyTypes)
  async getCompanyTypes({
    getState,
    patchState,
  }: StateContext<OrganizationStateModel>): Promise<void> {
    if (getState().companyTypes.length) {
      return;
    }

    try {
      const companyTypes = await this.api.invoke(getCompanyTypes);
      patchState({ companyTypes });
    } catch (error) {
      console.error('Failed to fetch company types', error);
    }
  }
}
